"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";
import { useCart } from "@/contexts/CartContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import Head from "next/head";
import { HIGH_INCOME_LOCATIONS } from "@/utils/seo";

export default function CheckoutPage() {
  const router = useRouter();
  const { cart, cartCount, cartTotal, clearCart } = useCart();
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [discount, setDiscount] = useState(0);

  const [shippingInfo, setShippingInfo] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setShippingInfo({ ...shippingInfo, [e.target.name]: e.target.value });
  };

  const handleApplyPromo = () => {
    if (promoCode.toUpperCase() === "HAPPY10") {
      setDiscount(cartTotal * 0.1);
      setPromoApplied(true);
      toast.success(
        <div className="flex items-center gap-2">
          <span className="text-lg">🎉</span>
          <div>
            <p className="font-bold text-sm">Promo code applied!</p>
            <p className="text-xs text-plum/60">10% discount added</p>
          </div>
        </div>,
        { duration: 3000, icon: null }
      );
    } else if (promoCode.trim()) {
      toast.error("Invalid promo code", { duration: 2000 });
    }
  };

  const handleRemovePromo = () => {
    setPromoCode("");
    setPromoApplied(false);
    setDiscount(0);
    toast.success("Promo code removed", { duration: 2000 });
  };

  const shippingCost = cartTotal >= 35 ? 0 : 4.99;
  const finalTotal = cartTotal + shippingCost - discount;

  const handlePlaceOrder = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const requiredFields = ["fullName", "email", "phone", "address", "city", "state", "zipCode"];
    const missingFields = requiredFields.filter((field) => !shippingInfo[field as keyof typeof shippingInfo]);

    if (missingFields.length > 0) {
      toast.error("Please fill in all required fields", { duration: 3000 });
      return;
    }

    // Simulate order placement
    toast.success(
      <div className="flex items-center gap-2">
        <span className="text-lg">✨</span>
        <div>
          <p className="font-bold text-sm">Order placed successfully!</p>
          <p className="text-xs text-plum/60">Thank you for your purchase</p>
        </div>
      </div>,
      { duration: 4000, icon: null }
    );

    // Clear cart and redirect
    setTimeout(() => {
      clearCart();
      router.push("/homepage");
    }, 2000);
  };

  // Redirect if cart is empty
  if (cart.length === 0) {
    return (
      <>
        <Header />
        <PageTransition>
          <main className="min-h-screen bg-gradient-to-br from-plum-50 via-lavender-50 to-white pt-28 pb-16">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center py-16">
              <div className="text-8xl mb-6">🛒</div>
              <h1 className="font-fraunces text-3xl font-bold text-plum mb-4">
                Your cart is empty
              </h1>
              <p className="text-plum/60 mb-8">Add some items to proceed to checkout</p>
              <Link href="/products" className="btn-primary inline-flex items-center gap-2 px-8 py-4 min-h-[48px]">
                <Icon name="ShoppingBagIcon" size={20} variant="solid" />
                Browse Products
              </Link>
            </div>
          </main>
        </PageTransition>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Head>
        <title>Secure Checkout | My Happy Place Digital - Trusted by Mothers Worldwide</title>
        <meta name="description" content={`Complete your secure checkout for premium children's books. Trusted by mothers in ${HIGH_INCOME_LOCATIONS.slice(0, 12).join(", ")}, and 288+ high-income areas. Free shipping over $35.`} />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://myhappypla4132.builtwithrocket.new/checkout" />
      </Head>
      <Header />
      <PageTransition>
        <main className="min-h-screen bg-gradient-to-br from-plum-50 via-lavender-50 to-white pt-28 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="font-fraunces text-4xl md:text-5xl font-bold text-plum mb-3">
                Checkout ✨
              </h1>
              <p className="text-plum/60 text-lg">Complete your magical order</p>
            </div>

            <form onSubmit={handlePlaceOrder}>
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Left Column - Forms */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Shipping Information */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="bg-white rounded-2xl shadow-dreamy p-6 md:p-8"
                  >
                    <h2 className="font-fraunces text-2xl font-bold text-plum mb-6 flex items-center gap-2">
                      <Icon name="TruckIcon" size={24} variant="outline" className="text-plum-light" />
                      Shipping Information
                    </h2>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-bold text-plum mb-2">
                          Full Name <span className="text-rose">*</span>
                        </label>
                        <input
                          type="text"
                          name="fullName"
                          value={shippingInfo.fullName}
                          onChange={handleInputChange}
                          placeholder="Jane Doe"
                          required
                          className="w-full bg-plum-50 border border-lavender/40 rounded-xl px-4 py-4 text-plum placeholder:text-plum/40 focus:outline-none focus:ring-2 focus:ring-lavender transition-all min-h-[48px]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-plum mb-2">
                          Email <span className="text-rose">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={shippingInfo.email}
                          onChange={handleInputChange}
                          placeholder="jane@example.com"
                          required
                          className="w-full bg-plum-50 border border-lavender/40 rounded-xl px-4 py-4 text-plum placeholder:text-plum/40 focus:outline-none focus:ring-2 focus:ring-lavender transition-all min-h-[48px]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-plum mb-2">
                          Phone <span className="text-rose">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={shippingInfo.phone}
                          onChange={handleInputChange}
                          placeholder="(555) 123-4567"
                          required
                          className="w-full bg-plum-50 border border-lavender/40 rounded-xl px-4 py-4 text-plum placeholder:text-plum/40 focus:outline-none focus:ring-2 focus:ring-lavender transition-all min-h-[48px]"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-bold text-plum mb-2">
                          Address <span className="text-rose">*</span>
                        </label>
                        <input
                          type="text"
                          name="address"
                          value={shippingInfo.address}
                          onChange={handleInputChange}
                          placeholder="123 Main Street, Apt 4B"
                          required
                          className="w-full bg-plum-50 border border-lavender/40 rounded-xl px-4 py-4 text-plum placeholder:text-plum/40 focus:outline-none focus:ring-2 focus:ring-lavender transition-all min-h-[48px]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-plum mb-2">
                          City <span className="text-rose">*</span>
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={shippingInfo.city}
                          onChange={handleInputChange}
                          placeholder="New York"
                          required
                          className="w-full bg-plum-50 border border-lavender/40 rounded-xl px-4 py-4 text-plum placeholder:text-plum/40 focus:outline-none focus:ring-2 focus:ring-lavender transition-all min-h-[48px]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-plum mb-2">
                          State <span className="text-rose">*</span>
                        </label>
                        <input
                          type="text"
                          name="state"
                          value={shippingInfo.state}
                          onChange={handleInputChange}
                          placeholder="NY"
                          required
                          className="w-full bg-plum-50 border border-lavender/40 rounded-xl px-4 py-4 text-plum placeholder:text-plum/40 focus:outline-none focus:ring-2 focus:ring-lavender transition-all min-h-[48px]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-plum mb-2">
                          ZIP Code <span className="text-rose">*</span>
                        </label>
                        <input
                          type="text"
                          name="zipCode"
                          value={shippingInfo.zipCode}
                          onChange={handleInputChange}
                          placeholder="10001"
                          required
                          className="w-full bg-plum-50 border border-lavender/40 rounded-xl px-4 py-4 text-plum placeholder:text-plum/40 focus:outline-none focus:ring-2 focus:ring-lavender transition-all min-h-[48px]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-plum mb-2">
                          Country <span className="text-rose">*</span>
                        </label>
                        <select
                          name="country"
                          value={shippingInfo.country}
                          onChange={handleInputChange}
                          required
                          className="w-full bg-plum-50 border border-lavender/40 rounded-xl px-4 py-4 text-plum focus:outline-none focus:ring-2 focus:ring-lavender transition-all min-h-[48px]"
                        >
                          <option value="">Select a country</option>
                          <option value="Afghanistan">Afghanistan</option>
                          <option value="Albania">Albania</option>
                          <option value="Algeria">Algeria</option>
                          <option value="Andorra">Andorra</option>
                          <option value="Angola">Angola</option>
                          <option value="Antigua and Barbuda">Antigua and Barbuda</option>
                          <option value="Argentina">Argentina</option>
                          <option value="Armenia">Armenia</option>
                          <option value="Australia">Australia</option>
                          <option value="Austria">Austria</option>
                          <option value="Azerbaijan">Azerbaijan</option>
                          <option value="Bahamas">Bahamas</option>
                          <option value="Bahrain">Bahrain</option>
                          <option value="Bangladesh">Bangladesh</option>
                          <option value="Barbados">Barbados</option>
                          <option value="Belarus">Belarus</option>
                          <option value="Belgium">Belgium</option>
                          <option value="Belize">Belize</option>
                          <option value="Benin">Benin</option>
                          <option value="Bhutan">Bhutan</option>
                          <option value="Bolivia">Bolivia</option>
                          <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                          <option value="Botswana">Botswana</option>
                          <option value="Brazil">Brazil</option>
                          <option value="Brunei">Brunei</option>
                          <option value="Bulgaria">Bulgaria</option>
                          <option value="Burkina Faso">Burkina Faso</option>
                          <option value="Burundi">Burundi</option>
                          <option value="Cabo Verde">Cabo Verde</option>
                          <option value="Cambodia">Cambodia</option>
                          <option value="Cameroon">Cameroon</option>
                          <option value="Canada">Canada</option>
                          <option value="Central African Republic">Central African Republic</option>
                          <option value="Chad">Chad</option>
                          <option value="Chile">Chile</option>
                          <option value="China">China</option>
                          <option value="Colombia">Colombia</option>
                          <option value="Comoros">Comoros</option>
                          <option value="Congo">Congo</option>
                          <option value="Costa Rica">Costa Rica</option>
                          <option value="Croatia">Croatia</option>
                          <option value="Cuba">Cuba</option>
                          <option value="Cyprus">Cyprus</option>
                          <option value="Czech Republic">Czech Republic</option>
                          <option value="Denmark">Denmark</option>
                          <option value="Djibouti">Djibouti</option>
                          <option value="Dominica">Dominica</option>
                          <option value="Dominican Republic">Dominican Republic</option>
                          <option value="East Timor">East Timor</option>
                          <option value="Ecuador">Ecuador</option>
                          <option value="Egypt">Egypt</option>
                          <option value="El Salvador">El Salvador</option>
                          <option value="Equatorial Guinea">Equatorial Guinea</option>
                          <option value="Eritrea">Eritrea</option>
                          <option value="Estonia">Estonia</option>
                          <option value="Eswatini">Eswatini</option>
                          <option value="Ethiopia">Ethiopia</option>
                          <option value="Fiji">Fiji</option>
                          <option value="Finland">Finland</option>
                          <option value="France">France</option>
                          <option value="Gabon">Gabon</option>
                          <option value="Gambia">Gambia</option>
                          <option value="Georgia">Georgia</option>
                          <option value="Germany">Germany</option>
                          <option value="Ghana">Ghana</option>
                          <option value="Greece">Greece</option>
                          <option value="Grenada">Grenada</option>
                          <option value="Guatemala">Guatemala</option>
                          <option value="Guinea">Guinea</option>
                          <option value="Guinea-Bissau">Guinea-Bissau</option>
                          <option value="Guyana">Guyana</option>
                          <option value="Haiti">Haiti</option>
                          <option value="Honduras">Honduras</option>
                          <option value="Hungary">Hungary</option>
                          <option value="Iceland">Iceland</option>
                          <option value="India">India</option>
                          <option value="Indonesia">Indonesia</option>
                          <option value="Iran">Iran</option>
                          <option value="Iraq">Iraq</option>
                          <option value="Ireland">Ireland</option>
                          <option value="Israel">Israel</option>
                          <option value="Italy">Italy</option>
                          <option value="Jamaica">Jamaica</option>
                          <option value="Japan">Japan</option>
                          <option value="Jordan">Jordan</option>
                          <option value="Kazakhstan">Kazakhstan</option>
                          <option value="Kenya">Kenya</option>
                          <option value="Kiribati">Kiribati</option>
                          <option value="Kosovo">Kosovo</option>
                          <option value="Kuwait">Kuwait</option>
                          <option value="Kyrgyzstan">Kyrgyzstan</option>
                          <option value="Laos">Laos</option>
                          <option value="Latvia">Latvia</option>
                          <option value="Lebanon">Lebanon</option>
                          <option value="Lesotho">Lesotho</option>
                          <option value="Liberia">Liberia</option>
                          <option value="Libya">Libya</option>
                          <option value="Liechtenstein">Liechtenstein</option>
                          <option value="Lithuania">Lithuania</option>
                          <option value="Luxembourg">Luxembourg</option>
                          <option value="Madagascar">Madagascar</option>
                          <option value="Malawi">Malawi</option>
                          <option value="Malaysia">Malaysia</option>
                          <option value="Maldives">Maldives</option>
                          <option value="Mali">Mali</option>
                          <option value="Malta">Malta</option>
                          <option value="Marshall Islands">Marshall Islands</option>
                          <option value="Mauritania">Mauritania</option>
                          <option value="Mauritius">Mauritius</option>
                          <option value="Mexico">Mexico</option>
                          <option value="Micronesia">Micronesia</option>
                          <option value="Moldova">Moldova</option>
                          <option value="Monaco">Monaco</option>
                          <option value="Mongolia">Mongolia</option>
                          <option value="Montenegro">Montenegro</option>
                          <option value="Morocco">Morocco</option>
                          <option value="Mozambique">Mozambique</option>
                          <option value="Myanmar">Myanmar</option>
                          <option value="Namibia">Namibia</option>
                          <option value="Nauru">Nauru</option>
                          <option value="Nepal">Nepal</option>
                          <option value="Netherlands">Netherlands</option>
                          <option value="New Zealand">New Zealand</option>
                          <option value="Nicaragua">Nicaragua</option>
                          <option value="Niger">Niger</option>
                          <option value="Nigeria">Nigeria</option>
                          <option value="North Korea">North Korea</option>
                          <option value="North Macedonia">North Macedonia</option>
                          <option value="Norway">Norway</option>
                          <option value="Oman">Oman</option>
                          <option value="Pakistan">Pakistan</option>
                          <option value="Palau">Palau</option>
                          <option value="Palestine">Palestine</option>
                          <option value="Panama">Panama</option>
                          <option value="Papua New Guinea">Papua New Guinea</option>
                          <option value="Paraguay">Paraguay</option>
                          <option value="Peru">Peru</option>
                          <option value="Philippines">Philippines</option>
                          <option value="Poland">Poland</option>
                          <option value="Portugal">Portugal</option>
                          <option value="Qatar">Qatar</option>
                          <option value="Romania">Romania</option>
                          <option value="Russia">Russia</option>
                          <option value="Rwanda">Rwanda</option>
                          <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
                          <option value="Saint Lucia">Saint Lucia</option>
                          <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option>
                          <option value="Samoa">Samoa</option>
                          <option value="San Marino">San Marino</option>
                          <option value="Sao Tome and Principe">Sao Tome and Principe</option>
                          <option value="Saudi Arabia">Saudi Arabia</option>
                          <option value="Senegal">Senegal</option>
                          <option value="Serbia">Serbia</option>
                          <option value="Seychelles">Seychelles</option>
                          <option value="Sierra Leone">Sierra Leone</option>
                          <option value="Singapore">Singapore</option>
                          <option value="Slovakia">Slovakia</option>
                          <option value="Slovenia">Slovenia</option>
                          <option value="Solomon Islands">Solomon Islands</option>
                          <option value="Somalia">Somalia</option>
                          <option value="South Africa">South Africa</option>
                          <option value="South Korea">South Korea</option>
                          <option value="South Sudan">South Sudan</option>
                          <option value="Spain">Spain</option>
                          <option value="Sri Lanka">Sri Lanka</option>
                          <option value="Sudan">Sudan</option>
                          <option value="Suriname">Suriname</option>
                          <option value="Sweden">Sweden</option>
                          <option value="Switzerland">Switzerland</option>
                          <option value="Syria">Syria</option>
                          <option value="Taiwan">Taiwan</option>
                          <option value="Tajikistan">Tajikistan</option>
                          <option value="Tanzania">Tanzania</option>
                          <option value="Thailand">Thailand</option>
                          <option value="Togo">Togo</option>
                          <option value="Tonga">Tonga</option>
                          <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                          <option value="Tunisia">Tunisia</option>
                          <option value="Turkey">Turkey</option>
                          <option value="Turkmenistan">Turkmenistan</option>
                          <option value="Tuvalu">Tuvalu</option>
                          <option value="Uganda">Uganda</option>
                          <option value="Ukraine">Ukraine</option>
                          <option value="United Arab Emirates">United Arab Emirates</option>
                          <option value="United Kingdom">United Kingdom</option>
                          <option value="United States">United States</option>
                          <option value="Uruguay">Uruguay</option>
                          <option value="Uzbekistan">Uzbekistan</option>
                          <option value="Vanuatu">Vanuatu</option>
                          <option value="Vatican City">Vatican City</option>
                          <option value="Venezuela">Venezuela</option>
                          <option value="Vietnam">Vietnam</option>
                          <option value="Yemen">Yemen</option>
                          <option value="Zambia">Zambia</option>
                          <option value="Zimbabwe">Zimbabwe</option>
                        </select>
                      </div>
                    </div>
                  </motion.div>

                  {/* Payment Section Placeholder */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                    className="bg-white rounded-2xl shadow-dreamy p-6 md:p-8"
                  >
                    <h2 className="font-fraunces text-2xl font-bold text-plum mb-6 flex items-center gap-2">
                      <Icon name="CreditCardIcon" size={24} variant="outline" className="text-plum-light" />
                      Payment Method
                    </h2>

                    <div className="bg-gradient-to-br from-plum-50 to-lavender-50 rounded-xl p-8 text-center border-2 border-dashed border-lavender/50">
                      <div className="text-5xl mb-4">💳</div>
                      <h3 className="font-fraunces text-xl font-bold text-plum mb-2">
                        Payment Integration Coming Soon
                      </h3>
                      <p className="text-plum/60 text-sm mb-6">
                        Secure payment processing will be available here
                      </p>
                      <div className="flex items-center justify-center gap-3 flex-wrap">
                        <div className="bg-white rounded-lg px-4 py-2 shadow-sm">
                          <span className="text-xs font-bold text-plum">💳 Cards</span>
                        </div>
                        <div className="bg-white rounded-lg px-4 py-2 shadow-sm">
                          <span className="text-xs font-bold text-plum">📱 Digital Wallets</span>
                        </div>
                        <div className="bg-white rounded-lg px-4 py-2 shadow-sm">
                          <span className="text-xs font-bold text-plum">🏦 Bank Transfer</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Right Column - Order Summary */}
                <div className="lg:col-span-1">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 }}
                    className="bg-white rounded-2xl shadow-dreamy p-6 sticky top-28"
                  >
                    <h2 className="font-fraunces text-2xl font-bold text-plum mb-6">
                      Order Summary
                    </h2>

                    {/* Cart Items */}
                    <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
                      {cart.map((item) => (
                        <div key={item.id} className="flex gap-3 pb-3 border-b border-lavender/20 last:border-0">
                          <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-plum-50 flex-shrink-0">
                            <AppImage src={item.image} alt={item.alt} fill className="object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-bold text-plum line-clamp-1">{item.title}</h3>
                            <p className="text-xs text-plum/60">Qty: {item.quantity}</p>
                            <p className="text-sm font-black text-plum">${(item.price * item.quantity).toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Promo Code */}
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-plum mb-2">Promo Code</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          placeholder="HAPPY10"
                          disabled={promoApplied}
                          className="flex-1 bg-plum-50 border border-lavender/40 rounded-xl px-3 py-2.5 text-sm text-plum placeholder:text-plum/40 focus:outline-none focus:ring-2 focus:ring-lavender transition-all disabled:opacity-50"
                        />
                        {promoApplied ? (
                          <button
                            type="button"
                            onClick={handleRemovePromo}
                            className="px-4 py-2.5 rounded-xl bg-rose/10 text-rose font-bold text-sm hover:bg-rose/20 transition-all active:scale-95"
                          >
                            Remove
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={handleApplyPromo}
                            className="px-4 py-2.5 rounded-xl bg-plum text-white font-bold text-sm hover:bg-plum-light transition-all active:scale-95"
                          >
                            Apply
                          </button>
                        )}
                      </div>
                      {promoApplied && (
                        <p className="text-xs text-green-600 font-semibold mt-2 flex items-center gap-1">
                          <Icon name="CheckCircleIcon" size={14} variant="solid" />
                          Promo code applied!
                        </p>
                      )}
                    </div>

                    {/* Pricing Breakdown */}
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-plum/70">
                        <span>Subtotal ({cartCount} items)</span>
                        <span className="font-bold">${cartTotal.toFixed(2)}</span>
                      </div>
                      {discount > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Discount</span>
                          <span className="font-bold">-${discount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-plum/70">
                        <span>Shipping</span>
                        <span className="font-bold text-green-600">
                          {shippingCost === 0 ? "FREE" : `$${shippingCost.toFixed(2)}`}
                        </span>
                      </div>
                      {cartTotal < 35 && (
                        <p className="text-xs text-plum/50 bg-plum-50 rounded-lg p-2">
                          🎉 Add ${(35 - cartTotal).toFixed(2)} more for free shipping!
                        </p>
                      )}
                      <div className="border-t border-lavender/30 pt-3 flex justify-between text-plum text-lg">
                        <span className="font-bold">Total</span>
                        <span className="font-black">${finalTotal.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Place Order Button */}
                    <button
                      type="submit"
                      className="w-full btn-primary py-4 text-base font-bold flex items-center justify-center gap-2 mb-3"
                    >
                      <Icon name="CheckCircleIcon" size={20} variant="solid" />
                      Place Order
                    </button>

                    <Link
                      href="/cart"
                      className="block text-center text-sm text-plum/70 hover:text-plum font-semibold transition-colors"
                    >
                      ← Back to Cart
                    </Link>
                  </motion.div>
                </div>
              </div>
            </form>
          </div>
        </main>
      </PageTransition>
      <Footer />
    </>
  );
}